import { FormsRepository } from "@common/domain/repositories/FormsRepository";
import Navigator from "@navigator/index";
import { IRouteParams, NavigationUrl, URLs } from "@navigator/NavigationUrl";
import {
  call,
  ForkEffect,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import {
  IAdvancedRulesClient,
  IAdvancedRulesServer,
  IFieldsMap,
  IForm,
  IFormLockData,
} from "@common/redux/formBuilder/interface";
import {
  addNewFormFailedAction,
  addNewFormSuccessAction,
  deleteFormFailedAction,
  deleteFormSuccessAction,
  duplicateFormFailureAction,
  duplicateFormSuccessAction,
  fetchFormsByIdsSuccessAction,
  fetchFormsFailedAction,
  fetchFormsSuccessAction,
  formsActions,
  updateFormFailedAction,
  updateFormSuccessAction,
  transformAndUpdateFormFailedAction,
  transformAndUpdateFormSuccessAction,
  createFormRuleSuccessAction,
  createFormRuleFailureAction,
  fetchFormRulesSuccessAction,
  fetchFormRulesFailureAction,
  updateFormRuleSuccessAction,
  updateFormRuleFailedAction,
  deleteFormRulesSuccessAction,
  deleteFormRulesFailedAction,
  fetchInfographicsDashBoardDataByIdSuccessAction,
  fetchInfographicsDashBoardDataByIdFailureAction,
  updateInfographicsDashBoardDataByIdSuccessAction,
  updateInfographicsDashBoardDataByIdFailureAction,
  addInfographicsDashBoardDataByIdSuccessAction,
  addInfographicsDashBoardDataByIdFailureAction,
  deleteInfographicsDashBoardDataByFormIdSuccessAction,
  deleteInfographicsDashBoardDataByFormIdFailureAction,
  deleteInfographicsDashBoardDataByFormIdAction,
  addFormAnalyticsSuccess,
  addFormAnalyticsFailure,
  getFormAnalyticsSuccess,
  getFormAnalyticsFailure,
  getInfographicsDataByQuerySuccessAction,
  getInfographicsDataByQueryFailureAction,
  getSuibmissionCountFailureAction,
  getSuibmissionCountSuccessAction,
  getSubmissionCountAction,
  fetchPermissionsByIdFailedAction,
  fetchPermissionsByIdSuccessAction,
  fetchWorkflowSuccessAction,
  fetchWorkflowFailureAction,
} from "./actions";
import {
  IAddNewFormAction,
  IDuplicateFormAction,
  IFetchFormsByIdsAction,
  IFetchFormsSuccessActionPayload,
  IFormServer,
  IUpdateFormAction,
  ITransformAndUpdateFormAction,
  IUpdateFormRuleAction,
  IFetchInfographicsDashBoardDataByIdAction,
  IInfographicsDashboardDataServer,
  IUpdateInfographicsDashBoardDataByIdAction,
  IInfographicsDashboardDataClient,
  IAddInfographicsDashBoardDataByIdAction,
  IAddFormAnalyticAction,
  IAddFormAnalyticPayload,
  IGetFormAnalyticAction,
  IGetFormAnalyticsData,
  IFetchInfographicsDataByQueryAction,
  IgetSubmissionCountAction,
  ISubmissionCount,
  IFetchOptionsForLookupFieldAction,
  IFetchPermissionByIdAction,
  ICustomPermission,
  ICustomPermissionServer,
  IFetchFormsAction,
  IFetchFormsByIdsFreeAction,
  IFetchOptionsForLookupFieldWithFilterActionPayload,
  IFetchOptionsForLookupFieldWithFilterAction,
  IAddNewFormActionPayload,
} from "./interface";
import {
  fetchFormIdsSelector,
  getInfographicsDashboardDataMapSelector,
} from "./selectors";
import {
  duplicateForm,
  EMPTY_FORM_DATA,
  transformFormDataClientToServer,
  transformInfographicsDashboardDataClientToServer,
  transformInfographicsDashboardDataServerToClient,
  transformRuleDataClientToServer,
  convertCustomPermissionServerToClient,
  encodeParams,
} from "./utils";
import { getTrasformedChartData } from "@presentation/Statistics/components/CustomizableDashboardCardGraph/getTrasformedChartData";
import { transformServerToClientTableData } from "@presentation/Statistics/components/BasicFiltersContainer/utils/transformServerToClientTableData";
import {
  getActiveFormIdSelector,
  getActiveRuleSelector,
  getFieldsMapOfActiveFormSelector,
} from "../formBuilder/selectors";
import { IWorkflowServer } from "../formSubmission/interface";
import { convertJsonNewToOutputJson } from "@presentation/FormBuilder/components/FormLookupField/utils";
import { onLoadUpdateFormLockDataAction } from "../formBuilder/actions";

const handleFormNavigation = (formId?: string) => {
  const IRouteParams: IRouteParams = {};
  IRouteParams.formId = formId;
  Navigator.push(NavigationUrl.generate(URLs.formBuilder, IRouteParams));
};

function* fetchFormsSaga(action: IFetchFormsAction) {
  try {
    const response: IFetchFormsSuccessActionPayload = yield call(
      FormsRepository.fetchForms,
      action.payload
    );
    const formIds = response.content.map((formData, index) => {
      return formData?.id;
    });
    yield put(getSubmissionCountAction(formIds));
    yield put(fetchFormsSuccessAction(response));
  } catch (e) {
    yield put(
      fetchFormsFailedAction({
        error: e.message,
        pageNumber: action.payload?.pageNumber,
      })
    );
    action?.payload?.onFailure?.();
  }
}

function* fetchFormRulesSaga(action) {
  try {
    const { formId } = action.payload;
    const response: IAdvancedRulesClient = yield call(
      FormsRepository.fetchFormRules,
      formId
    );
    yield put(fetchFormRulesSuccessAction(response));
  } catch (e) {
    yield put(fetchFormRulesFailureAction(e.message));
  }
}

export function* fetchWorkflowSaga(action) {
  try {
    const { formId } = action.payload;
    const response: IWorkflowServer[] = yield call(
      FormsRepository.fetchWorkflows,
      formId
    );
    yield put(fetchWorkflowSuccessAction(response));
  } catch (e) {
    yield put(fetchWorkflowFailureAction(e.message));
  }
}

function* addNewFormSaga(action: IAddNewFormAction) {
  try {
    const emptyForm = action.payload;
    const response: IFormServer = yield call(
      FormsRepository.addNewForm,
      emptyForm
    );
    yield put(addNewFormSuccessAction(response));
    handleFormNavigation(response.id);
  } catch (e) {
    yield put(addNewFormFailedAction(e.message));
  }
}

function* duplicateFormSaga(action: IDuplicateFormAction) {
  try {
    const { formId } = action?.payload;
    const response: IFormServer = yield call(
      FormsRepository?.addNewForm,
      EMPTY_FORM_DATA
    );
    const duplicateFormId: string = response?.id;
    const formToDuplicate: IFormServer = yield call(
      FormsRepository?.fetchFormByIds,
      [formId]
    );
    const duplicatedForm: IFormServer = duplicateForm(
      formToDuplicate,
      duplicateFormId
    );
    const updatedDuplicateForm: IFormServer = yield call(
      FormsRepository?.updateForms,
      duplicatedForm
    );
    yield put(duplicateFormSuccessAction(updatedDuplicateForm));
  } catch (e) {
    yield put(duplicateFormFailureAction(e?.message));
  }
}

function* updateFormSaga(action: IUpdateFormAction) {
  try {
    const formData: IFormServer = action.payload;
    const response: IFormServer = yield call(
      FormsRepository.updateForms,
      formData
    );
    yield put(updateFormSuccessAction(response));
  } catch (e) {
    yield put(updateFormFailedAction(e.message));
  }
}

function* updateFormRulesSaga(action: IUpdateFormRuleAction) {
  try {
    const formRuleDataClient: IAdvancedRulesClient = action.payload;
    const activeFormId: string = yield select(getActiveFormIdSelector);
    const formRuleDataServer: IAdvancedRulesServer =
      transformRuleDataClientToServer(formRuleDataClient, activeFormId);
    const response: IAdvancedRulesServer = yield call(
      FormsRepository.updateFormRules,
      formRuleDataServer
    );
    yield put(updateFormRuleSuccessAction(response));
  } catch (e) {
    yield put(updateFormRuleFailedAction(e.message));
  }
}

function* transformAndUpdateFormSaga(action: ITransformAndUpdateFormAction) {
  const lookupValues = [];
  Object.values(action.payload?.fieldsMap)
    .filter(
      (field) =>
        field.fieldType === "LOOKUP" &&
        field.fieldValue.sourceForm.content !== ""
    ) // Filter for LOOKUP field types where content is not empty
    .forEach((field) => {
      const value = field.fieldValue.sourceForm.value;
      if (!lookupValues.includes(value)) {
        // Check for uniqueness before adding
        lookupValues.push(value);
      }
    });

  const temp = { ...action.payload };
  temp.relatedFormIds = lookupValues;
  try {
    const formData: IForm = temp;
    const response: IFormServer = yield call(
      FormsRepository.updateForms,
      transformFormDataClientToServer(formData)
    );
    response["enableFieldNumbering"] = formData.enableFieldNumbering; // need to be removed, once backend added this field
    yield put(transformAndUpdateFormSuccessAction(response));
  } catch (e) {
    yield put(transformAndUpdateFormFailedAction(e.message));
  }
}

export function* fetchFormByIdsSaga(action: IFetchFormsByIdsAction) {
  try {
    const formIds: string[] = action?.payload?.formIds ?? [];
    const response: IFormServer = yield call(
      FormsRepository.fetchFormByIds,
      formIds
    );
    const formsList = [response]; //we're recieving response as object but needed as list.
    if (response?.rules) {
      const rulesPayload = {
        ...response?.rules,
        formId: response.id,
      };
      yield put(fetchFormRulesSuccessAction(rulesPayload));
    }
    yield put(fetchFormsByIdsSuccessAction(formsList));
    const formId = response.id;
    const isFormLocked = response.isFormLocked;
    const formLockedBy = response.formLockedBy;

    const isFormLockedArray: any = {};
    const formLockedByArray: any = {};
    const formLockDetails: IFormLockData = {
      formId: formId,
      isFormLocked: {},
      formLockedBy: {},
    };

    isFormLockedArray[formId] = isFormLocked;
    formLockedByArray[formId] = formLockedBy;
    formLockDetails.isFormLocked = isFormLockedArray;
    formLockDetails.formLockedBy = formLockedByArray;

    if (window.location.href.includes("form-builder")) {
      yield put(onLoadUpdateFormLockDataAction(formLockDetails));
    }
  } catch (e) {
    yield put(fetchFormsFailedAction(e.message));
  }
}
export function* fetchFormByIdsFreeSaga(action: IFetchFormsByIdsFreeAction) {
  try {
    const formIds: string[] = action.payload.formIds;
    const onSuccess = action.payload.onSuccess;
    const response: IFormServer = yield call(
      FormsRepository.fetchFormByIds,
      formIds
    );
    const formsList = [response];
    yield put(fetchFormsByIdsSuccessAction(formsList));
    onSuccess(formsList);
  } catch (e) {
    const onFailure = action.payload.onFailure;
    onFailure(e.message);
  }
}
export function* fetchPermissionByIdSaga(action: IFetchPermissionByIdAction) {
  try {
    const id: string = action.payload;
    const roleName = action.roleName;
    const response: ICustomPermissionServer[] = yield call(
      FormsRepository.fetchPermissionById,
      roleName
    );
    const customPermission: ICustomPermission =
      convertCustomPermissionServerToClient(response);

    yield put(fetchPermissionsByIdSuccessAction(customPermission));
  } catch (e) {
    yield put(fetchPermissionsByIdFailedAction(e.message));
  }
}

function* deleteFormSaga(action) {
  try {
    const index: number = action.payload.formIndex;
    const existingFormIds: string[] = yield select(fetchFormIdsSelector);
    const formId: string = existingFormIds[index];

    const response = yield call(FormsRepository.deleteForm, formId);
    yield put(deleteFormSuccessAction(index));
  } catch (e) {
    yield put(deleteFormFailedAction(e.message));
  }
}

function* createFormRulesSaga(action: IUpdateFormRuleAction) {
  try {
    const formRuleDataClient: IAdvancedRulesClient = action.payload;
    const activeFormId: string = yield select(getActiveFormIdSelector);
    const formRuleDataServer: IAdvancedRulesServer =
      transformRuleDataClientToServer(formRuleDataClient, activeFormId);
    const response: IAdvancedRulesServer = yield call(
      FormsRepository.createFormRules,
      formRuleDataServer
    );
    yield put(createFormRuleSuccessAction(response));
  } catch (e) {
    yield put(createFormRuleFailureAction(e.message));
  }
}
function* deleteInfographicsDashBoardDataByFormIdSaga(action) {
  try {
    const formId = action.payload;
    const response: IAdvancedRulesServer = yield call(
      FormsRepository.deleteFormDashBoardDataByFormId,
      formId
    );
    yield put(deleteInfographicsDashBoardDataByFormIdSuccessAction(formId));
  } catch (e) {
    yield put(deleteInfographicsDashBoardDataByFormIdFailureAction(e.message));
  }
}

function* deleteFormRulesSaga(action) {
  try {
    const { formId, formRuleData, ruleType, index } = action.payload;
    let response;
    const rule = formRuleData?.[formId];
    const ruleData = [...((rule && rule[ruleType]) ?? [])];
    const activeRules: IAdvancedRulesClient = yield select(
      getActiveRuleSelector
    );
    const fieldRules = activeRules?.fieldRules || [];
    const formRules = activeRules?.formRules || [];
    const pageRules = activeRules?.pageRules || [];
    const submissionRules = activeRules?.submissionRules || [];

    if (
      fieldRules.length > 0 ||
      formRules.length > 0 ||
      pageRules.length > 0 ||
      submissionRules.length > 0
    ) {
      const updatedFormRule = ruleData.filter((rule, i) => i != index);
      const updatedFormRules = {
        ...formRuleData,
        ...formRuleData[formId],
      };
      updatedFormRules[ruleType] = updatedFormRule;
      const fieldRules = updatedFormRules?.fieldRules || [];
      const formRules = updatedFormRules?.formRules || [];
      const pageRules = updatedFormRules?.pageRules || [];
      const submissionRules = updatedFormRules?.submissionRules || [];
      if (
        fieldRules.length == 0 &&
        formRules.length == 0 &&
        pageRules.length == 0 &&
        submissionRules.length == 0
      ) {
        response = yield call(FormsRepository.deleteFormRules, formId);
        yield put(deleteFormRulesSuccessAction(formId));
      }
      response = yield call(FormsRepository.updateFormRules, updatedFormRules);
      yield put(updateFormRuleSuccessAction(response));
    } else {
      response = yield call(FormsRepository.deleteFormRules, formId);
      yield put(deleteFormRulesSuccessAction(formId));
    }
  } catch (e) {
    yield put(deleteFormRulesFailedAction(e.message));
  }
}

export function* fetchInfographicsDashBoardDataByIdSaga(
  action: IFetchInfographicsDashBoardDataByIdAction
) {
  try {
    const formId: string = action.payload;
    const response: IInfographicsDashboardDataServer = yield call(
      FormsRepository.fetchFormDashBoardDataById,
      formId
    );
    const infographicsDataClient: IInfographicsDashboardDataClient =
      transformInfographicsDashboardDataServerToClient(response);
    if (!infographicsDataClient.formId) {
      infographicsDataClient.formId = formId;
    }
    yield put(
      fetchInfographicsDashBoardDataByIdSuccessAction(infographicsDataClient)
    );
  } catch (e) {
    yield put(fetchInfographicsDashBoardDataByIdFailureAction(e.message));
  }
}

export function* updateInfographicsDashBoardDataByIdSaga(
  action: IUpdateInfographicsDashBoardDataByIdAction
) {
  try {
    let infographicsData: IInfographicsDashboardDataClient =
      action.payload?.infographicsData; // if we get infographics data in props,
    const formId = action.payload?.formId; // if we get only formId in props,
    if (formId) {
      const infographicsDataMap: IInfographicsDashboardDataClient =
        yield select(getInfographicsDashboardDataMapSelector);
      infographicsData = infographicsDataMap[formId];
    }
    const infographicsDataServer: IInfographicsDashboardDataServer =
      transformInfographicsDashboardDataClientToServer(infographicsData);
    if (infographicsDataServer.data.length == 0) {
      const formId = infographicsDataServer.formId;

      yield put(deleteInfographicsDashBoardDataByFormIdAction(formId));
    } else {
      const response: IInfographicsDashboardDataServer = yield call(
        FormsRepository.updateFormDashBoardDataById,
        infographicsDataServer
      );
      const infographicsDataClient: IInfographicsDashboardDataClient =
        transformInfographicsDashboardDataServerToClient(response);
      if (!infographicsDataClient.formId) {
        infographicsDataClient.formId = infographicsData?.formId;
      }
      yield put(
        updateInfographicsDashBoardDataByIdSuccessAction(infographicsDataClient)
      );
    }
  } catch (e) {
    yield put(updateInfographicsDashBoardDataByIdFailureAction(e.message));
  }
}

export function* addInfographicsDashBoardDataByIdSaga(
  action: IAddInfographicsDashBoardDataByIdAction
) {
  try {
    const infographicsData: IInfographicsDashboardDataClient =
      action.payload?.infographicsData;
    const infographicsDataServer: IInfographicsDashboardDataServer =
      transformInfographicsDashboardDataClientToServer(infographicsData);
    const response: IInfographicsDashboardDataServer = yield call(
      FormsRepository.addFormDashBoardDataById,
      infographicsDataServer
    );
    const infographicsDataClient: IInfographicsDashboardDataClient =
      transformInfographicsDashboardDataServerToClient(response);
    if (!infographicsDataClient.formId) {
      infographicsDataClient.formId = infographicsData?.formId;
    }
    yield put(
      addInfographicsDashBoardDataByIdSuccessAction(infographicsDataClient)
    );
  } catch (e) {
    yield put(addInfographicsDashBoardDataByIdFailureAction(e.message));
  }
}

export function* addInfographicsAnalyticsSaga(action: IAddFormAnalyticAction) {
  try {
    const formAnalyticData: IAddFormAnalyticPayload = action.payload;
    const response: IInfographicsDashboardDataServer = yield call(
      FormsRepository.addInfographicsAnalytics,
      formAnalyticData
    );

    yield put(addFormAnalyticsSuccess());
  } catch (e) {
    yield put(addFormAnalyticsFailure(e.message));
  }
}

export function* getInfographicsAnalyticsSaga(action: IGetFormAnalyticAction) {
  try {
    const formId: string = action.payload;
    const response: IGetFormAnalyticsData = yield call(
      FormsRepository.getInfographicsAnalytics,
      formId
    );
    const successData = { [formId]: response };
    yield put(getFormAnalyticsSuccess(successData));
  } catch (e) {
    yield put(getFormAnalyticsFailure(e.message));
  }
}

export function* getInfographicsDataByQuerySaga(
  action: IFetchInfographicsDataByQueryAction
) {
  try {
    const widgetId: string = action.payload.widegetId;
    const formId = action.payload.formId;
    //need to ask
    const [state, fieldsMap] = yield select((state) => [
      state?.forms?.dashboardDataMap?.[formId]?.infographicsWidgetMap ?? {},
      state?.forms?.formsMap[formId]?.fields,
    ]);
    const fieldsMapData: IFieldsMap = yield select(
      getFieldsMapOfActiveFormSelector
    );
    let widgetType = action.payload.widgetType;
    if (widgetType == "GRAPH") {
      widgetType = "CHART";
    }
    const payload = {
      formId: action.payload.formId,
      query: action.payload.query,
      widgetType: widgetType,
    };
    const response: any = yield call(
      FormsRepository.getInfographicsDataByQuery,
      payload
    );
    let successData = {};
    if (
      action.payload.widgetType === "GRAPH" ||
      action.payload.widgetType === "CHART"
    ) {
      const data = getTrasformedChartData(response);
      successData = { [widgetId]: data };
    } else {
      const data = transformServerToClientTableData(
        { [widgetId]: response },
        state[widgetId],
        fieldsMapData
      );
      successData = { [widgetId]: data };
    }
    yield put(getInfographicsDataByQuerySuccessAction(successData));
  } catch (e) {
    yield put(getInfographicsDataByQueryFailureAction(e.message));
  }
}

export function* getFormSubmissionCountSaga(action: IgetSubmissionCountAction) {
  try {
    const formIds = action?.payload || [];
    const response: ISubmissionCount[] = yield call(
      FormsRepository.getSubmissionCount,
      formIds
    );
    yield put(getSuibmissionCountSuccessAction(response));
  } catch (e) {
    yield put(getSuibmissionCountFailureAction(e.message));
  }
}

function* fetchOptionsForLookupField(
  action: IFetchOptionsForLookupFieldAction
) {
  const { onSuccess, onFailure, formId, fieldIds, filterData } = action.payload;
  try {
    // const response = yield call(
    //   FormsRepository.fetchOptionsForLookupField,
    //   fieldIds,
    //   formId
    // );
    const stringifiedFilter = JSON.stringify(filterData?.filter);
    const responseNew = yield call(
      FormsRepository.fetchOptionsForLookupFieldByFIlter,
      formId,
      stringifiedFilter
    );
    onSuccess(responseNew);
  } catch (error) {
    onFailure(error.message);
  }
}
function* fetchOptionsForLookupFieldWithFilter(
  action: IFetchOptionsForLookupFieldWithFilterAction
) {
  const {
    onSuccess,
    onFailure,
    formId,
    fieldIds,
    filterData,
    query,
    pageSize,
    pageNumber,
    additionalConditions
  } = action.payload;
  try {
    const payload = {
      formId: formId,
      query: query,
      pageSize: pageSize,
      pageNumber: pageNumber,
    additionalConditions:additionalConditions,
    };
    const response: any = yield call(
      FormsRepository.fetchOptionsForLookupFieldWithFilter,
      payload
    );
    onSuccess(response);
  } catch (error) {
    onFailure(error.message);
  }
}

function* fetchOptionsForLookupFieldWithFilterOfPublicApi(
  action: IFetchOptionsForLookupFieldWithFilterAction
) {
  const {
    onSuccess,
    onFailure,
    formId,
    fieldIds,
    filterData,
    query,
    orgId,
    pageSize,
    pageNumber,
    additionalConditions
  } = action.payload;

  try {
    const payload = {
      formId: formId,
      pageSize: pageSize,
      pageNumber: pageNumber,
      query: query,
      orgId: orgId,
    additionalConditions:additionalConditions,

    };
    const response: any = yield call(
      FormsRepository.fetchOptionsForLookupFieldWithFilterOfPublicApi,
      payload
    );
    onSuccess(response);
  } catch (error) {
    onFailure(error.message);
  }
}

function* createFormUsingTemplate(action: any) {
  try {
    const formData = action?.payload;
    const templateId = formData?.sourceTemplateId;

    // Run all 3 API calls concurrently using Promise.allSettled
    const results = yield call(() =>
      Promise.allSettled([
        FormsRepository.addNewForm(formData), // 1. Add new form
        FormsRepository.fetchWorkflows(templateId), // 2. Fetch workflows
        FormsRepository.fetchFormRules(templateId), // 3. Fetch form rules
      ])
    );

    // Handle addNewForm result (first API)
    const addNewFormResult = results[0];
    if (addNewFormResult.status === "fulfilled") {
      const formId = addNewFormResult.value.id;
      yield put(addNewFormSuccessAction(addNewFormResult.value));

      // Handle fetchWorkflows result (second API)
      const fetchWorkflowsResult = results[1];
      if (fetchWorkflowsResult.status === "fulfilled") {
        yield put(fetchWorkflowSuccessAction(fetchWorkflowsResult.value));
      } else {
        yield put(
          fetchWorkflowFailureAction(fetchWorkflowsResult.reason.message)
        );
      }

      // Handle fetchFormRules result (third API)
      const fetchFormRulesResult = results[2];
      if (
        fetchFormRulesResult.status === "fulfilled" &&
        fetchFormRulesResult.value.formId !==
          `Rules not found for formId: ${templateId}`
      ) {
        yield put(fetchFormRulesSuccessAction(fetchFormRulesResult.value));
        try {
          const formRuleDataClient: IAdvancedRulesClient = action.payload;
          const formRuleDataServer: IAdvancedRulesServer =
            transformRuleDataClientToServer(formRuleDataClient, formId);
          const response: IAdvancedRulesServer = yield call(
            FormsRepository.createFormRules,
            formRuleDataServer
          );
          yield put(createFormRuleSuccessAction(response));
        } catch (e) {
          yield put(createFormRuleFailureAction(e.message));
        }
      } else {
        yield put(
          fetchFormRulesFailureAction(fetchFormRulesResult.value.formId)
        );
      }
      handleFormNavigation(formId);
    } else {
      yield put(addNewFormFailedAction(addNewFormResult.reason.message));
      return; // Exit early if form creation fails
    }
  } catch (error) {
    console.error("Error in createFormUsingTemplate saga:", error);
  }
}

export function* watchForms(): Generator<ForkEffect> {
  yield takeLatest(formsActions.ADD_NEW_FORM, addNewFormSaga);
  yield takeLatest(formsActions.UPDATE_FORM, updateFormSaga);
  yield takeLatest(
    formsActions.TRANSFORM_AND_UPDATE_FORM,
    transformAndUpdateFormSaga
  );
  yield takeLatest(formsActions.DELETE_FORM, deleteFormSaga);
  yield takeEvery(formsActions.FETCH_FORMS, fetchFormsSaga);
  yield takeLatest(formsActions.FETCH_FORM_BY_IDS, fetchFormByIdsSaga);
  yield takeLatest(formsActions.FETCH_FORM_BY_IDS_FREE, fetchFormByIdsFreeSaga);
  yield takeLatest(
    formsActions.FETCH_PERMISSION_BY_ID,
    fetchPermissionByIdSaga
  );
  yield takeLatest(formsActions.DUPLICATE_FORM, duplicateFormSaga);
  yield takeLatest(formsActions.CREATE_FORM_RULES, createFormRulesSaga);
  yield takeLatest(formsActions.FETCH_FORM_RULES, fetchFormRulesSaga);
  yield takeLatest(formsActions.FETCH_WORKFLOW, fetchWorkflowSaga);
  yield takeLatest(formsActions.UPDATE_FORM_RULES, updateFormRulesSaga);
  yield takeLatest(formsActions.DELETE_FORM_RULES, deleteFormRulesSaga);
  yield takeLatest(
    formsActions.FETCH_INFOGRAPHICS_DASHBOARD_DATA_BY_ID,
    fetchInfographicsDashBoardDataByIdSaga
  );
  yield takeLatest(
    formsActions.UPDATE_INFOGRAPHICS_DASHBOARD_DATA_BY_ID,
    updateInfographicsDashBoardDataByIdSaga
  );
  yield takeLatest(
    formsActions.ADD_INFOGRAPHICS_DASHBOARD_DATA_BY_ID,
    addInfographicsDashBoardDataByIdSaga
  );

  yield takeLatest(
    formsActions.DELETE_INFOGRAPHICS_DASHBOARD_DATA_BY_FORMID,
    deleteInfographicsDashBoardDataByFormIdSaga
  );
  yield takeLatest(
    formsActions.ADD_FORM_ANALYTICS,
    addInfographicsAnalyticsSaga
  );
  yield takeLatest(
    formsActions.GET_FORM_ANALYTICS,
    getInfographicsAnalyticsSaga
  );
  yield takeEvery(
    formsActions.GET_INFOGRAPHIC_DATA_BY_QUERY,
    getInfographicsDataByQuerySaga
  );
  yield takeEvery(
    formsActions.GET_FORM_SUBMISSION_COUNT,
    getFormSubmissionCountSaga
  );
  yield takeEvery(
    formsActions.FETCH_OPTIONS_FOR_LOOKUP_FIELD,
    fetchOptionsForLookupField
  );
  yield takeEvery(
    formsActions.FETCH_OPTIONS_FOR_LOOKUP_FIELD_WITH_FILTER,
    fetchOptionsForLookupFieldWithFilter
  );
  yield takeEvery(
    formsActions.FETCH_OPTIONS_FOR_LOOKUP_FIELD_WITH_FILTER_PUBLIC_API,
    fetchOptionsForLookupFieldWithFilterOfPublicApi
  );
  yield takeEvery(
    formsActions.CREATE_FORM_USING_TEMPLATE,
    createFormUsingTemplate
  );
}
